'use client';

import { FC } from 'react';
import classNames from 'classnames';
import { Document } from '@contentful/rich-text-types';
import { EntryFields } from 'contentful';
import { ConditionalRichText } from '@/components/common/ConditionalRichText/ConditionalRichText';
import useCurrentPromo from '@/hooks/useCurrentPromo';
import type { TypePromotionCampaign } from '@/core/generated-types';
import PromoSlotInlineSkeleton from './PromoSlotInlineSkeleton';
import { normalizeToRichText } from './utils';

/** Slot placement identifiers */
export type PromoSlotPdpSlotId =
  | 'pdp-simple'
  | 'pdp-top-option'
  | 'pdp-play-kit-details';

export interface PromoSlotPdpProps {
  /** Unique identifier for the slot placement */
  slotId: PromoSlotPdpSlotId;
  /** When true, uses plkMessage instead of pdpMessage */
  isPlayKit?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Direct message override - bypasses promotion fetch entirely.
   * Accepts plain strings (including HTML), Rich Text Documents, Contentful RichText, or undefined.
   * When provided, the slot renders this message instead of fetching from promotion.
   */
  overrideMessage?: string | Document | EntryFields.RichText;
  /** Text color class when using message override */
  textColor?: string;
  /** When true, returns null promotion without fetching */
  disabled?: boolean;
  /** Override skeleton display state */
  shouldShowSkeleton?: boolean;
  /** Override promotion data */
  promotion?: TypePromotionCampaign | null;
}

/**
 * PromoSlotPdp - Unified PDP promo slot component.
 * Displays pdpMessage or plkMessage (for Play Kits) from the active PromotionCampaign.
 *
 * Usage:
 * 1. Default: Fetches and displays promotion message
 *    <PromoSlotPdp slotId="pdp-simple" />
 *
 * 2. With message override (no fetch):
 *    <PromoSlotPdp slotId="pdp-top-option" overrideMessage={bfcmText} textColor="text-red" />
 *
 * Message fields accept plain strings (including HTML), Rich Text Documents, or undefined.
 *
 * @param slotId - Unique identifier for the slot placement (used in data-testid)
 * @param isPlayKit - When true, uses plkMessage instead of pdpMessage
 * @param overrideMessage - Direct message override, bypasses promotion fetch
 * @param textColor - Text color class when using message override
 * @param className - Additional CSS classes
 */
const PromoSlotPdp: FC<PromoSlotPdpProps> = ({
  slotId,
  isPlayKit,
  className,
  overrideMessage,
  textColor,
  disabled,
  shouldShowSkeleton: shouldShowSkeletonOverride,
  promotion,
}) => {
  // If overrideMessage is provided, skip the fetch entirely
  const skipFetch = overrideMessage !== undefined;
  const { promotion: promoCampaign, shouldShowSkeleton } = useCurrentPromo({
    disabled: skipFetch || disabled,
    shouldShowSkeleton: shouldShowSkeletonOverride,
    promotion,
  });

  // Handle direct message override (no fetch needed)
  if (overrideMessage !== undefined) {
    const normalizedMessage = normalizeToRichText(overrideMessage);
    if (!normalizedMessage) return null;

    return (
      <div
        className={classNames(textColor, className)}
        data-testid={`promo-slot-${slotId}`}
      >
        <ConditionalRichText
          content={normalizedMessage}
          addParagraphMargins={false}
        />
      </div>
    );
  }

  if (shouldShowSkeleton) {
    return (
      <PromoSlotInlineSkeleton className={classNames('h-6 mb-4', className)} />
    );
  }

  const promoMessage = normalizeToRichText(
    isPlayKit
      ? promoCampaign?.fields?.plkMessage
      : promoCampaign?.fields?.pdpMessage,
  );
  if (!promoMessage) return null;

  return (
    <div
      className={classNames(
        'mb-4',
        promoCampaign?.fields?.textColor,
        className,
      )}
      data-testid={`promo-slot-${slotId}`}
    >
      <ConditionalRichText content={promoMessage} addParagraphMargins={false} />
    </div>
  );
};

export default PromoSlotPdp;
